import {
  ButtonPrimary,
  ButtonTextPrimary,
  ButtonWithLoadingIndicator,
  CancelButton,
  ControlledInput,
} from "../components-atoms";
/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Checkbox, Container, FormControl, FormControlLabel, FormLabel, Grid, InputLabel, ListItemText, MenuItem, Modal, Radio, RadioGroup, Select, Typography } from "@material-ui/core";
import {
  CreatePushNotificationRequestModel,
  createCustomerPushNotification,
  createPushNotification,
} from "../redux/actions/createPushNotification";
import { ITheme } from "../constants/theme";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { GeneralModal } from ".";
import { makeStyles } from "@material-ui/styles";
import { useCallback, useMemo, useState } from "react";
import { Caption } from "../components-atoms/Caption";
import { IShopByMerchantResponse } from "../redux/reducers/getShopNames";
import { ControlledFileInput } from "../components-atoms/ControlledFileInput";
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { getDateFormat, getKeyboardPickerDateTimeFormat } from "../utils/dateTimeFormatting";

const TEMPLATE_CSV_FILE__URL = "https://tradekart.azureedge.net/tradekart/notification/Customer_Notification_Template.csv";
export enum PushNotificationTargetType {
  Shop = "Shop",
  Customer = "Customer"
}

export enum PushNotificationScheduleType {
  Now = "Now",
  Scheduled = "Scheduled"
}

interface IProps {
  visible: boolean;
  onClose: () => void;
  shops: IShopByMerchantResponse[];
}
export const AddPushNotificationModal = ({
  visible,
  onClose,
  shops,
}: IProps) => {
  const [selectedShops, setSelectedShops] = useState<string[]>([]);
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    handleSubmit,
    formState: { isValid, errors },
    reset,
    setValue,
    control,
    setError,
    getValues
  } = useForm<any>({ reValidateMode: "onBlur", mode: "onBlur" });
  const [targetType, setTargetType] = useState<PushNotificationTargetType>(PushNotificationTargetType.Shop);
  const [scheduleType, setScheduleType] = useState<PushNotificationScheduleType>(PushNotificationScheduleType.Now);
  const loading = useSelector(
    (state: any) => state.CreatePushNotificationReducer.loading
  );
  const isDisabled = !isValid || loading || Object.keys(errors).length > 0 ||
    ((targetType === PushNotificationTargetType.Shop && selectedShops.length === 0)
      || (targetType === PushNotificationTargetType.Customer && !getValues('CustomerIds')))

  const onSubmit: SubmitHandler<CreatePushNotificationRequestModel> = (
    data
  ) => {
    switch (targetType) {
      case PushNotificationTargetType.Shop:
        dispatch(
          createPushNotification(
            {
              Title: data.Title,
              Body: data.Body,
              Shops: selectedShops,
              ScheduleDate: data.ScheduleDate || null
            },
            () => {
              onClose();
              reset();
              setSelectedShops([]);
            }
          )
        );
        break;
      case PushNotificationTargetType.Customer:
        const formData = new FormData();
        formData.append("Title", data.Title);
        formData.append("Body", data.Body);
        formData.append("CustomerIds", getValues('CustomerIds'));
        if (data.ScheduleDate) {
          formData.append("ScheduleDate", data.ScheduleDate);
        }
        dispatch(
          createCustomerPushNotification(formData, () => {
            onClose();
            reset();
            setValue('CustomerIds', undefined);
          })
        );
        break;
    }
  };

  const selectAll = (selected: boolean) => {
    let val: string[] = [];
    if (selected) {
      const ids = shops.flatMap(merchant => merchant.Shops.map(shop => shop.ShopId));
      val = ids;
    }

    setSelectedShops(val as any);
    setValue("Shops", val, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  const handleMerchantChange = (merchantId, isChecked) => {
    const merchant = shops.find((m) => m.MerchantId === merchantId);
    const shopIds = merchant?.Shops.map((shop) => shop.ShopId) || [];

    setSelectedShops((prevSelected) => {
      if (isChecked) {
        // Add all shop IDs of the merchant to the selected list
        const newIds = [...prevSelected, ...shopIds.filter((id) => !prevSelected.includes(id))];
        setValue("Shops", newIds, {
          shouldDirty: true,
          shouldValidate: true,
        });
        return newIds;
      } else {
        // Remove all shop IDs of the merchant from the selected list
        const newIds = prevSelected.filter((id) => !shopIds.includes(id));
        setValue("Shops", newIds, {
          shouldDirty: true,
          shouldValidate: true,
        });
        return newIds;
      }
    });
  };

  const handleShopChange = (shopId, isChecked) => {
    setSelectedShops((prevSelected) => {
      if (isChecked) {
        // Add shop ID to the selected list
        const newIds = [...prevSelected, shopId];
        setValue("Shops", newIds, {
          shouldDirty: true,
          shouldValidate: true,
        });
        return newIds;
      } else {
        // Remove shop ID from the selected list
        const newIds = prevSelected.filter((id) => id !== shopId);
        setValue("Shops", newIds, {
          shouldDirty: true,
          shouldValidate: true,
        });
        return newIds;
      }
    });
  };

  const isMerchantSelected = useMemo(() => {
    return (merchantId) => {
      const merchant = shops.find((m) => m.MerchantId === merchantId);
      if (!merchant) return false;

      const shopIds = merchant?.Shops?.map((shop) => shop.ShopId);
      return shopIds?.every((id) => selectedShops.includes(id));
    };
  }, [shops, selectedShops]);

  const isMerchantIndeterminate = useMemo(() => {
    return (merchantId) => {
      const merchant = shops.find((m) => m.MerchantId === merchantId);
      if (!merchant) return false;

      const shopIds = merchant?.Shops?.map((shop) => shop.ShopId);
      const anySelected = shopIds?.some((id) => selectedShops.includes(id));
      const allSelected = shopIds?.every((id) => selectedShops.includes(id));
      return anySelected && !allSelected;
    };
  }, [shops, selectedShops]);

  const isShopSelected = useMemo(() => {
    return (shopId) => selectedShops.includes(shopId);
  }, [selectedShops]);

  const handleTargetTypeChange = (event) => {
    setTargetType(event.target.value);
  };

  const handleScheduleTypeChange = (event) => {
    event.target.value === PushNotificationScheduleType.Now ?
      setValue('ScheduleDate', undefined)
      : setValue('ScheduleDate', new Date().toISOString());
    setScheduleType(event.target.value);
  }

  const getScheduleMaxDate = useCallback(
    () => {
      const nextWeekUTC = new Date();
      nextWeekUTC.setUTCDate(nextWeekUTC.getUTCDate() + 7);
      return nextWeekUTC.toISOString();
    },
    [visible],
  )

  const renderValue = useCallback(() => {
    if (selectedShops.length > 0) {
      const selectedShopNames = shops
        .flatMap(merchant =>
          merchant?.Shops
            .filter(shop => selectedShops.includes(shop.ShopId))
            .map(shop => shop.ShopName)
        );
      return (
        <Box display="flex" width="362px" height="38px" marginTop="-8px" marginBottom="-44px">
          <Typography
            style={{ padding: 8, paddingBottom: 0 }}
            noWrap={true}
            variant="body2"
            component="span"
            color="textPrimary"
          >
            {selectedShopNames.join(', ')}
          </Typography>
        </Box>
      );
    }
    return null;
  }, [shops, selectedShops]);

  const renderDescription = () => {
    return (
      <div className={classes.description}>
        <Typography variant="body2">
          Prepare your notification and determine to whom it will be sent.
        </Typography>
        <Typography variant="body2">
          You can add emojis to your notifications using emoji shortcuts on your keyboard when entering the title and the notification message.
        </Typography>
        <Typography variant="body2">
          Shortcut key combination: Windows – Windows logo key + . and on a Mac – Control + Command + Space
        </Typography>
        <Typography variant="body2">
          You can download the template CSV file from here: <a href={TEMPLATE_CSV_FILE__URL}>Customer List CSV</a>
        </Typography>
      </div>
    )
  }

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Modal
        open={visible}
        onClose={() => {
          reset();
          onClose();
        }}
        className={classes.modal}>
        <Container className={classes.modalContainer}>
          <Grid container spacing={1}>
            <Grid item>
              <Typography
                variant="h6"
                component="h6"
                color="textPrimary"
              >
                Send Push Notification
              </Typography>
            </Grid>
            <Grid item style={{ overflow: 'auto', maxHeight: '80vh', paddingTop: 16 }}>
              {renderDescription()}
              <form className={classes.form}>
                <fieldset className={classes.fieldset}>
                  <ControlledInput
                    placeholder="Notification Title"
                    label="Notification Title"
                    name="Title"
                    control={control}
                    error={errors["Title"]}
                    onChange={(e) => {
                      if (e.target.value.length > 65) {
                        setError("Title", { type: "maxLength" });
                        return;
                      }
                      setValue("Title", e.target.value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    helperText={`${getValues('Title')?.length ?? 0}/65`}
                    FormHelperTextProps={{
                      style: {
                        textAlign: 'right'
                      },
                    }}
                    rules={{ required: true, maxLength: 65 }}
                  />
                  <ControlledInput
                    multiline
                    placeholder="Notification Message"
                    label="Notification Message"
                    name="Body"
                    helperText={`${getValues('Body')?.length ?? 0}/240`}
                    FormHelperTextProps={{
                      style: {
                        textAlign: 'right'
                      },
                    }}
                    control={control}
                    error={errors["Body"]}
                    onChange={(e) => {
                      if (e.target.value.length > 240) {
                        setError("Body", { type: "maxLength" });
                        return;
                      }
                      setValue("Body", e.target.value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    rules={{ required: true, maxLength: 240 }}
                  />

                  <FormControl component="fieldset">
                    <FormLabel component="legend" id="targetType">Target Type</FormLabel>
                    <RadioGroup
                      name="targetType"
                      value={targetType}
                      onChange={handleTargetTypeChange}
                      className={classes.radioGroup}>
                      <FormControlLabel value={PushNotificationTargetType.Shop} control={<Radio color="primary" />} label="Shop" />
                      <FormControlLabel value={PushNotificationTargetType.Customer} control={<Radio color="primary" />} label="Customer" />
                    </RadioGroup>
                  </FormControl>

                  {targetType === PushNotificationTargetType.Shop && (
                    <FormControl fullWidth variant="outlined" style={{ minWidth: 300 }}>
                      <InputLabel id="select-shop">Targeted Shops</InputLabel>
                      <Select
                        labelId="select-shop"
                        label="Targeted Shops"
                        multiple
                        value={selectedShops}
                        renderValue={() => renderValue()}>
                        {shops?.map((merchant) => (
                          <Box key={merchant.MerchantId}>
                            <MenuItem>
                              <Checkbox
                                color="primary"
                                indeterminate={isMerchantIndeterminate(merchant.MerchantId)}
                                checked={isMerchantSelected(merchant.MerchantId)}
                                onChange={(e) =>
                                  handleMerchantChange(merchant.MerchantId, e.target.checked)
                                }
                              />
                              <ListItemText primary={merchant.MerchantName} />
                            </MenuItem>
                            {merchant?.Shops?.map((shop) => (
                              <MenuItem key={shop.ShopId} style={{ paddingLeft: 32 }}>
                                <Checkbox
                                  color="primary"
                                  checked={isShopSelected(shop.ShopId)}
                                  onChange={(e) => handleShopChange(shop.ShopId, e.target.checked)}
                                />
                                <ListItemText primary={shop.ShopName} />
                              </MenuItem>
                            ))}
                          </Box>
                        ))}
                      </Select>
                      <Caption style={{ margin: 4, fontWeight: 600 }}>You can select multiple shops</Caption>
                    </FormControl>
                  )}

                  {targetType === PushNotificationTargetType.Shop && (
                    <Box className={classes.selectButtonContainer}>
                      <ButtonPrimary
                        onClick={() => selectAll(true)}>
                        Select All
                      </ButtonPrimary>
                      <ButtonTextPrimary
                        onClick={() => selectAll(false)}
                        disabled={selectedShops.length === 0} >
                        Deselect All
                      </ButtonTextPrimary>
                    </Box>
                  )}

                  {targetType === PushNotificationTargetType.Customer && (
                    <ControlledFileInput
                      buttonText="Select customer list"
                      acccept=".csv"
                      onFileChange={(file) => {
                        setValue("CustomerIds", file, { shouldDirty: true, shouldValidate: true });
                      }}
                    />
                  )}

                  <FormControl component="fieldset" style={{ marginTop: 32, width: '100%' }}>
                    <FormLabel component="legend" id="scheduleType">Schedule Type</FormLabel>
                    <RadioGroup
                      name="scheduleType"
                      value={scheduleType}
                      onChange={handleScheduleTypeChange}
                      className={classes.radioGroup}>
                      <FormControlLabel value={PushNotificationScheduleType.Now} control={<Radio color="primary" />} label="Now" />
                      <FormControlLabel value={PushNotificationScheduleType.Scheduled} control={<Radio color="primary" />} label="Scheduled" />
                    </RadioGroup>
                    {scheduleType === PushNotificationScheduleType.Scheduled && (
                      <KeyboardDateTimePicker
                        label="Schedule Date"
                        placeholder={getDateFormat()}
                        format={getKeyboardPickerDateTimeFormat()}
                        variant="dialog"
                        margin="normal"
                        fullWidth
                        inputVariant="outlined"
                        InputProps={{
                          readOnly: true
                        }}
                        onChange={(d) => {
                          if (d) {
                            d.setSeconds(0);
                            setValue('ScheduleDate', d.toISOString(),
                              {
                                shouldDirty: true,
                                shouldValidate: true
                              })
                          }
                        }}
                        value={getValues('ScheduleDate')}
                        minDate={new Date()}
                        maxDate={getScheduleMaxDate()}
                      />
                    )}
                  </FormControl>
                </fieldset>
              </form>
            </Grid>
          </Grid>
          <GeneralModal.ButtonArea>
            <CancelButton
              disabled={loading}
              onClick={() => {
                reset();
                onClose();
                setSelectedShops([]);
              }}
            />
            <ButtonWithLoadingIndicator
              disabled={isDisabled}
              type="submit"
              title="Send"
              data-automation="add-button"
              loading={loading}
              onClick={handleSubmit(onSubmit)}
            />
          </GeneralModal.ButtonArea>
        </Container>
      </Modal>
    </MuiPickersUtilsProvider>
  )
};

const useStyles = makeStyles((theme: ITheme) => ({
  modal: { alignItems: "center", justifyContent: "center" },
  modalContainer: {
    width: "480px",
    height: "auto",
    backgroundColor: "white",
    boxShadow:
      "0px 6px 36px rgba(0, 0, 0, 0.15), 0px 2.21728px 14.2392px rgba(0, 0, 0, 0.111884), 0px 0.93179px 6.99399px rgba(0, 0, 0, 0.0997219), 0px 0.37078px 3.36556px rgba(0, 0, 0, 0.0894965), 0px 0.120827px 1.42249px rgba(0, 0, 0, 0.0753326), 0px 0.0225271px 0.422798px rgba(0, 0, 0, 0.0508791)",
    borderRadius: "20px",
    padding: "24px",
    margin: "0",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  cssOutlinedInput: {
    display: "flex",
    "&$cssFocused $notchedOutline": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  cssFocused: {},
  notchedOutline: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  fieldset: {
    marginTop: 4,
    padding: 0,
    border: 0,
  },
  fieldsetLegend: {
    paddingLeft: "14px",
  },
  prefix: {
    color: theme.palette.action.disabled,
    marginRight: 5,
    marginTop: 2,
  },
  suffix: {
    color: theme.palette.action.disabled,
  },
  form: {
    width: '100%',
  },
  selectButtonContainer: {
    display: 'flex',
    width: '100%',
    gap: 16,
    marginTop: 16,
    justifyContent: 'flex-end'
  },
  radioGroup: {
    flexDirection: 'row',
    gap: '16px',
    marginTop: '4px',
    marginBottom: '16px'
  },
  description: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 8
  }
}));